import {
  Literal,
  Null,
  Optional,
  Array as RArray,
  Boolean as RBoolean,
  Number as RNumber,
  Record,
  Static,
  String,
} from "runtypes";

import { DisplayTableOutputColumnTypeLiteral } from "../../../display-table/index.js";
import { DataConnectionTypeLiteral } from "../../../dataConnectionTypes.js";
import {
  DataConnectionId,
  DataSourceTableId,
  HexId,
  SemanticDatasetId,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticViewId,
} from "../../../idTypeBrands.js";
import { Nullish } from "../../../MagicTypes.js";

/**
 * Shared types used across agent tool results
 */

export const TableItem = Record({
  tableId: Optional(DataSourceTableId),
  description: Nullish(String),
  dataConnectionId: Optional(DataConnectionId),
  connectionName: Optional(String),
  connectionType: Optional(DataConnectionTypeLiteral),
  name: String,
  schema: String.Or(Null),
  database: String.Or(Null),
  endorsed: RBoolean,
});
export type TableItem = Static<typeof TableItem>;

export const LinkItem = Record({
  title: String,
  url: String,
});
export type LinkItem = Static<typeof LinkItem>;

export const SemanticColumnItem = Record({
  columnName: String,
  columnTitle: String.Or(Null), // optional, human readable title of a column item.
  columnDescription: String.Or(Null),
  columnKind: Literal("dimension").Or(Literal("measure")),
  columnExpression: String.Or(Null),
  columnType: String,
  canBeAggregated: RBoolean,
  datasetName: String,
  datasetId: SemanticDatasetId,
  semanticProjectId: SemanticProjectId,
  semanticProjectName: String,
  endorsed: RBoolean,
  statusName: Optional(String.Or(Null)),
  availableInViews: Optional(
    RArray(
      Record({
        semanticViewId: SemanticViewId,
        semanticViewName: String,
        semanticViewTitle: String.Or(Null),
        semanticViewDescription: String.Or(Null),
      }),
    ),
  ),
});
export type SemanticColumnItem = Static<typeof SemanticColumnItem>;

export const RelatedProject = Record({
  hexId: HexId,
  title: String.Or(Null),
});
export type RelatedProject = Static<typeof RelatedProject>;

/**
 * Semantic project item showing an overview.
 * For example, used by `inspectSemanticData` (mode 1).
 */
export const SemanticProjectItem = Record({
  semanticProjectId: SemanticProjectId,
  semanticProjectName: String,
  semanticProjectDescription: Optional(String),
  datasetCount: RNumber,
  viewCount: RNumber,
});
export type SemanticProjectItem = Static<typeof SemanticProjectItem>;

/**
 * Semantic resource (dataset or view) item
 * (does not show inner items like dimensions, measures)
 * For example, used by `inspectSemanticData` (mode 2).
 */
export const SemanticResourceItem = Record({
  semanticProjectId: SemanticProjectId,
  semanticProjectName: String,
  resourceType: Literal("dataset").Or(Literal("view")),
  resourceId: String, // SemanticDatasetId or SemanticViewId
  resourceName: String,
  resourceDescription: Optional(String),
  endorsed: RBoolean,
  statusName: Optional(String.Or(Null)),
  // For datasets only
  datasetId: Optional(SemanticDatasetId),
  // For views only
  viewId: Optional(SemanticViewId),
});
export type SemanticResourceItem = Static<typeof SemanticResourceItem>;

/**
 * Represents a join edge between two semantic datasets.
 * Used for visualizing join graphs in Mode 2 (full graph) and Mode 3 (local neighborhood).
 */
export const JoinEdge = Record({
  /** The source dataset name */
  from: SemanticDatasetName,
  /** The target dataset name */
  to: SemanticDatasetName,
  /** Optional name of the join relationship */
  joinName: Optional(String),
});
export type JoinEdge = Static<typeof JoinEdge>;

/**
 * Local join graph neighborhood for a specific dataset (Mode 3).
 * Shows incoming and outgoing join edges from the focal dataset.
 */
export const LocalJoinGraph = Record({
  /** The focal dataset this neighborhood is centered on */
  focalDataset: SemanticDatasetName,
  /** The project name for context */
  projectName: String,
  /** Edges where the focal dataset is the source (joins to other datasets) */
  outgoingEdges: RArray(JoinEdge),
  /** Edges where the focal dataset is the target (other datasets join to this one) */
  incomingEdges: RArray(JoinEdge),
});
export type LocalJoinGraph = Static<typeof LocalJoinGraph>;

/**
 * Column data for data preview tables.
 * Used by SQLAndPreviewTableResult.rows and SemanticDatasetResult for consistency with TablePreviewAgGrid.
 */
export const DataPreviewColumn = Record({
  /** Unique identifier for the column */
  id: String,
  /** Display name of the column */
  columnName: String,
  /** Data type of the column (e.g., STRING, NUMBER, etc.) */
  columnType: DisplayTableOutputColumnTypeLiteral,
  /** Array of string values for each row in this column */
  columnData: RArray(String),
});
export type DataPreviewColumn = Static<typeof DataPreviewColumn>;
